import { useTranslation } from "react-i18next";

/**
 * Hover-pause marquee of merchant logos.
 *
 * Drop logo files (SVG or transparent PNG) into
 *   /app/frontend/public/merchants/
 * and list them in MERCHANTS. Every logo renders inside a soft frosted tile so
 * brand logos with mixed colours (dark text on transparent, white wordmarks,
 * full-colour) all read cleanly on the dark hero background.
 */
const MERCHANTS = [
  // For merchants with both a light-bg and dark-bg variant, set BOTH
  //   file      → used in LIGHT mode (typically dark text on transparent)
  //   fileDark  → used in DARK mode  (typically white/light text on transparent)
  // When only `file` is provided, the same asset is used in both themes.
  { file: "tanger-med.png", fileDark: "tanger-med-light.png", name: "Tanger Med Port Authority" },
  { file: "ikea.png", name: "IKEA" },
  { file: "alain-afflelou.png", name: "Alain Afflelou" },
  { file: "marh.png", name: "MARH Coffee Roasters" },
  { file: "cecit.png", name: "CECIT" },
  { file: "crescents.png", name: "Crescents" },
  { file: "copenhagen.png", name: "Copenhagen" },
  { file: "casto-house.png", name: "Casto House" },
  { file: "palmucci.png", name: "Palmucci" },
  { file: "moojood.png", name: "Moojood" },
  { file: "tendys.png", name: "Tendys Tanger" },
];

const LOGO_HEIGHT = 32; // px — every logo is scaled to this height inside the tile

const LogoTile = ({ entry }) => {
  const lightSrc = entry.file ? `/merchants/${entry.file}` : null;
  const darkSrc = entry.fileDark ? `/merchants/${entry.fileDark}` : lightSrc;
  return (
    <div
      className="flex h-14 items-center justify-center rounded-lg border border-foreground/5 bg-foreground/[0.04] px-5 backdrop-blur transition duration-200 hover:bg-foreground/[0.08] hover:border-foreground/10"
      title={entry.name}
    >
      {lightSrc ? (
        <>
          {/* Light-mode variant */}
          <img
            src={lightSrc}
            alt={entry.name}
            className="block dark:hidden opacity-90 transition-opacity duration-200 hover:opacity-100"
            style={{ height: LOGO_HEIGHT, width: "auto", maxWidth: 160, objectFit: "contain" }}
            loading="lazy"
          />
          {/* Dark-mode variant — may be the same file when no darkFile provided */}
          <img
            src={darkSrc}
            alt={entry.name}
            className="hidden dark:block opacity-90 transition-opacity duration-200 hover:opacity-100"
            style={{ height: LOGO_HEIGHT, width: "auto", maxWidth: 160, objectFit: "contain" }}
            loading="lazy"
          />
        </>
      ) : (
        <span className="text-xs font-medium uppercase tracking-[0.18em] text-foreground/70 whitespace-nowrap">
          {entry.name}
        </span>
      )}
    </div>
  );
};

export const MerchantLogoStrip = () => {
  const { t } = useTranslation();
  if (!MERCHANTS.length) return null;

  // Render the list twice so the CSS marquee loops seamlessly.
  const loop = [...MERCHANTS, ...MERCHANTS];

  return (
    <div
      className="mt-14 w-full"
      data-testid="merchant-logo-strip"
      aria-label={t("hero.trustedBy")}
    >
      <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.32em] text-muted-foreground">
        {t("hero.trustedBy")}
      </p>
      <div className="marquee group relative overflow-hidden">
        {/* Edge fade-out gradients */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-background to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-background to-transparent z-10" />

        <div className="marquee-track flex items-center gap-4 group-hover:[animation-play-state:paused]">
          {loop.map((m, i) => (
            <LogoTile key={`${m.name}-${i}`} entry={m} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default MerchantLogoStrip;
